import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  PropsWithChildren,
} from 'react';

import ConnectedAttachmentsViewer from './ConnectedAttachmentsViewer';
import { AttachmentsViewerContext } from './context';
import { hasPreview } from '../../api/attachments';
import { handleNonViewableAttachment } from '../../api/openAttachmentByType';
import { VIEWER_TYPES } from '../../consts';
import { useAttachmentsContext } from '../../context/AttachmentsContext';
import type { Attachment } from '../../types';

export type AttachmentsViewerProviderProps = PropsWithChildren & {
  /** Controlled attachment ID — when set, displays this attachment */
  controlledAttachmentId?: string;
  /**
   * Callback fired when the viewer opens or closes — by `controlledAttachmentId`,
   * by a row click, or by `useOpenAttachment()`. Fires at most once per direction
   * per viewer session: switching attachments while the viewer stays open does not
   * re-fire `true`, and every `true` is paired with exactly one `false` on close.
   * A consumer syncing a URL param never sees a repeated or unmatched signal.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Callback fired when a non-previewable file is handled outside the viewer
   * (PDF opens in a new tab, or the file downloads). Fires from two paths:
   * - `useOpenAttachment()` — always, since that hook handles every file type
   * - `controlledAttachmentId` — only when `shouldAutoHandleNonViewable` is true
   */
  onFileHandled?: () => void;
  /**
   * When true, mimics click behavior: opens PDFs in new tab and downloads non-previewable files.
   * When false (default), only opens previewable files in viewer; others do nothing.
   *
   * IMPORTANT: Conservative default (false) is a safety feature. When a non-previewable
   * attachment is deep-linked with the default, it is silently ignored:
   * - No viewer opens
   * - No callback fires
   * - No URL-clearing signal is sent
   * This prevents unexpected downloads or PDF opens on page load.
   *
   * If you need to handle non-previewable files, either:
   * 1. Set shouldAutoHandleNonViewable={true} and use onFileHandled callback
   * 2. Implement your own detection logic (e.g., polling attachment load status)
   *
   * Use true for deep-linking scenarios where you want full click behavior.
   */
  shouldAutoHandleNonViewable?: boolean;
};

const AttachmentsViewerProvider: FC<AttachmentsViewerProviderProps> = ({
  children,
  controlledAttachmentId,
  onOpenChange,
  onFileHandled,
  shouldAutoHandleNonViewable = false,
}) => {
  const {
    attachments,
    downloadAttachment,
    openPDFAttachmentInNewTab,
    contextualTrack,
  } = useAttachmentsContext();
  const [currentZoomLevel, setCurrentZoomLevel] = useState(100);
  const [open, setOpen] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState<
    Attachment | undefined
  >(undefined);
  const processedIdRef = useRef<string | undefined>(undefined);
  const onOpenChangeRef = useRef(onOpenChange);
  const onFileHandledRef = useRef(onFileHandled);
  // Tracks whether an unmatched `true` has been reported, so switching
  // attachments mid-session cannot fire a second one.
  const openReportedRef = useRef(false);

  const reportViewerOpened = useCallback(() => {
    if (openReportedRef.current) return;
    openReportedRef.current = true;
    onOpenChangeRef.current?.(true);
  }, []);

  const reportFileHandled = useCallback(() => {
    onFileHandledRef.current?.();
  }, []);

  const viewableAttachments = useMemo(
    () => attachments.filter(hasPreview),
    [attachments],
  );

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
    onFileHandledRef.current = onFileHandled;
  }, [onOpenChange, onFileHandled]);

  // Handle controlled attachment ID - behaves like a real click (preview/PDF/download)
  // Conservative default: non-previewable files are silently ignored (no callback, no action)
  // Opt-in behavior: set shouldAutoHandleNonViewable={true} to open PDFs/trigger downloads
  useEffect(() => {
    if (
      controlledAttachmentId &&
      controlledAttachmentId !== processedIdRef.current
    ) {
      const attachment = attachments.find(
        (a) => a.attachmentId === controlledAttachmentId,
      );
      if (attachment) {
        processedIdRef.current = controlledAttachmentId;

        if (hasPreview(attachment)) {
          setCurrentAttachment(attachment);
          setOpen(true);
          contextualTrack('prodex.attachments.open_in_viewer', {
            file_type: attachment.fileType,
            viewer: VIEWER_TYPES.INTERNAL,
          });
          setCurrentZoomLevel(100);
          reportViewerOpened();
        } else if (shouldAutoHandleNonViewable) {
          // Opt-in: handle PDFs and downloads programmatically
          handleNonViewableAttachment({
            attachment,
            openPDFAttachmentInNewTab,
            downloadAttachment,
          });
          reportFileHandled();
        }
        // Conservative default: non-previewable files do nothing (intentional safety feature)
      }
    } else if (!controlledAttachmentId) {
      processedIdRef.current = undefined;
    }
  }, [
    controlledAttachmentId,
    attachments,
    downloadAttachment,
    openPDFAttachmentInNewTab,
    contextualTrack,
    shouldAutoHandleNonViewable,
    reportViewerOpened,
    reportFileHandled,
  ]);

  const currentAttachmentIndex = useMemo(
    () =>
      viewableAttachments.findIndex(
        (attachment) =>
          attachment.attachmentId === currentAttachment?.attachmentId,
      ),
    [viewableAttachments, currentAttachment?.attachmentId],
  );

  const viewNextAttachment = useCallback(() => {
    if (currentAttachmentIndex === viewableAttachments.length - 1) {
      setCurrentAttachment(viewableAttachments[0]);
    } else {
      setCurrentAttachment(viewableAttachments[currentAttachmentIndex + 1]);
    }

    setCurrentZoomLevel(100);
  }, [currentAttachmentIndex, viewableAttachments]);

  const viewPreviousAttachment = useCallback(() => {
    if (currentAttachmentIndex === 0) {
      setCurrentAttachment(viewableAttachments[viewableAttachments.length - 1]);
    } else {
      setCurrentAttachment(viewableAttachments[currentAttachmentIndex - 1]);
    }

    setCurrentZoomLevel(100);
  }, [currentAttachmentIndex, viewableAttachments]);

  const openViewer = useCallback(
    (attachmentId: string) => {
      setCurrentAttachment(
        viewableAttachments.find(
          (attachment) => attachment.attachmentId === attachmentId,
        ),
      );
      setOpen(true);
      reportViewerOpened();
    },
    [viewableAttachments, reportViewerOpened],
  );

  const zoomIn = useCallback(() => {
    setCurrentZoomLevel((prev) => (prev < 250 ? prev + 15 : prev));
  }, []);

  const zoomOut = useCallback(() => {
    setCurrentZoomLevel((prev) => (prev > 50 ? prev - 15 : prev));
  }, []);

  const closeViewer = useCallback(() => {
    setOpen(false);
    setCurrentAttachment(undefined);
    setCurrentZoomLevel(100);
    if (openReportedRef.current) {
      openReportedRef.current = false;
      onOpenChangeRef.current?.(false);
    }
  }, []);

  return (
    <AttachmentsViewerContext.Provider
      value={{
        open,
        currentAttachment,
        openViewer,
        closeViewer,
        reportFileHandled,
        viewNextAttachment,
        viewPreviousAttachment,
        totalAttachments: viewableAttachments.length,
        currentAttachmentIndex,
        currentZoomLevel,
        zoomIn,
        zoomOut,
      }}
    >
      {children}
      <ConnectedAttachmentsViewer />
    </AttachmentsViewerContext.Provider>
  );
};

export default AttachmentsViewerProvider;
